import { Inject, Injectable, OnDestroy, Optional, SkipSelf } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";

import { BehaviorSubject, Observable, Subscription, interval, throwError } from 'rxjs';
import { mergeMap, timeout } from 'rxjs/operators';

import { Events, EventBusService } from '../../../technical-modules/events/services/event-bus.service';

import { BatchsStatus } from '../entities/batchs-status.entity';
import { OngoingBatchDto } from '../entities/ongoing-batch-dto.entity';

@Injectable({ providedIn: 'root' })
export class OngoingBatchService implements OnDestroy {
	private path: string;
	private batchsSubject = new BehaviorSubject<OngoingBatchDto[]>([] as OngoingBatchDto[]);
	private timerSubscription: Subscription | null = null;
	
	constructor( //
		@Inject(EventBusService) eventBusService: EventBusService, //
		@Inject(HttpClient) private httpClient: HttpClient, //
		@Optional() @SkipSelf() previousInstance?: OngoingBatchService //
	) {
		if (previousInstance) {
			throw new Error(
				'This service is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;
		
		// Monitorer les demandes de mise a jour des informations de batchs
		eventBusService.observe(Events.RefreshBatchIndicatorRequested, () => this.loadAndSurveyUserBatchs());
		
		// Le chargement doit avoir lieu une fois a l'initialisation du service, event ou pas
		this.loadAndSurveyUserBatchs();
	}
	
	// Charge les batchs de l'utilisateur, et declence le debut de leur surveillance si necessaire
	private loadAndSurveyUserBatchs(): void {
		// Quand une telle demande arrive, on doit lancer la requete vers le serveur
		let batchs$ = this.getUserBatchs();
		
		batchs$.subscribe({
			next: (batchs) => {
				// A chaque nouveau resultat, on emet ce resutat dans le sujet associe
				// NB: La raison pour laquelle on ne renvoie pas directement l'observable de la requete
				// est que chaque .subscribe fait dessus duplique la requete, ce qui est anti-performant
				this.batchsSubject.next(batchs);

				// Evaluer s'il y a des batchs en cours a surveiller
				let needSurvey = !this.timerSubscription && batchs.some($ => $.status != 'COMPLETED' && $.status != 'FAILED');

				// Si c'est le cas 
				if (needSurvey) {
					// Activer la surveillance
					this.surveyBatchs();
				}
			},
			error: (errorData) => {
				console.error("OngoingBatchService - " + new Date() + " - ERROR!", errorData);
			}
		});
	};

	// Active la surveillance reguliere des batchs de l'utilisateur
	private surveyBatchs(): void {
		// Ne rien faire si la surveillance est deja active
		if (this.timerSubscription) {
			return;
		}
		
		// Programmer un timer de mise a jour
		this.timerSubscription = interval(5000)
			.pipe(
				mergeMap(
					(_activationCount) => this.getUserBatchs()
				)
			)
			.subscribe({
				next: (batchs) => {
					// A chaque nouveau resultat, on emet ce resutat dans le sujet associe
					// NB: La raison pour laquelle on ne renvoie pas directement l'observable de la requete
					// est que chaque .subscribe fait dessus duplique la requete, ce qui est anti-performant
					this.batchsSubject.next(batchs);
					
					// Determiner si la surveillance doit continuer
					let needSurvey = batchs.some($ => $.status != 'COMPLETED' && $.status != 'FAILED');
					
					// Si ce n'est pas la cas
					if (!needSurvey) {
						// Mettre fin a la surveillance
						this.unsurveyBatchs();
					}
				},
				error: (errorData) => {
					console.error("OngoingBatchService - " + new Date() + " - ERROR!", errorData);
				}
			})
		;
	}
	
	// Desactive la surveillance reguliere des batchs
	private unsurveyBatchs(): void {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
			this.timerSubscription = null;
		}
	}

	ngOnDestroy() {
		// A la destruction du composant, il faut arreter de monitorer les changements d'etat des batchs
		this.unsurveyBatchs();
	}
	
	// Methode servant a requeter les batchs de l'utilisateur
	private getUserBatchs(): Observable<OngoingBatchDto[]> {
		let url = this.path + 'users/current/ongoing-batchs';
		
		// On ajoute un header special pour empecher l'intercepteur d'afficher un indicateur de chargement
		let headers = new HttpHeaders().set("no-pending-trace", "true");
		
		let batchs = this.httpClient.get<OngoingBatchDto[]>(url, { headers: headers });
		return batchs.pipe(
			timeout(3000)
		);
	}

	// Methode pour maintenir les indicateurs de batch consistant a requeter toutes les 5s
	public observeBatchsForUser(): Observable<OngoingBatchDto[]> {
		// Renvoyer le sujet dans lequel on emet ces valeurs en resultat, pour permettre des subscribes dessus
		return this.batchsSubject;
	}

	public clear(ids: number[]): Observable<number> {
		let url = this.path + 'batchs/clear-orders';
		let result = this.httpClient.post<number>(url, ids);
		return result;
	}
}